import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { BallMover } from '../../../../../main/types/asset/asset.enums.ts';

import { BALL_ANIMATION_DEFAULT_TO_X_POSITION } from '../../../../constants/ball.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible, setInteractiveMode, setupBallAnimation } from '../../../../slices/ball/ball.slice.ts';
import { completeStepThunk } from '../../../../slices/progress/thunks/complete-step.thunk.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Base handler for app sound pronunciation tasks (letter sounds or word phonemes)
 * Provides shared functionality for AppPronounce and AppSoundOut handlers
 */
export abstract class AppSaySoundBaseHandler extends BaseAppStateChangeHandlerUtil {
  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);

    // Hide ball initially
    this.resetBallPosition();

    // Play step audio
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(true));
  }

  protected override async onPlaying(): Promise<void> {
    console.log(`${this.getHandlerName()} onPlaying`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);

    // Get audio duration for ball animation
    const duration = await this.audioPlayerUtil.getStepPronunciationDuration();

    // Child classes will implement their specific audio playback
    await this.playAudio();

    // Set up ball animation
    if (step.ballMover === BallMover.User) {
      // Enable interactive mode (sets draggable and visible)
      dispatch(setInteractiveMode(true));
    } else if (await this.shouldAnimateBall()) {
      // Set animation with visibility and draggability in a single action
      dispatch(
        setupBallAnimation({
          sequence: [
            {
              toXPosition: BALL_ANIMATION_DEFAULT_TO_X_POSITION,
              duration: this.calculateAnimationDuration(duration),
            },
          ],
        })
      );
    } else {
      // Just ensure the ball is visible
      dispatch(setBallIsVisible(true));
    }

    // Wait for audio to complete
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Complete the step and move to next state
    await dispatch(completeStepThunk()).unwrap();
    await dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyForNextStep, blockAnimation: true })).unwrap();
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    await this.handleReadyForNextStep();
  }

  // Abstract methods to be implemented by child classes
  protected abstract playAudio(): Promise<void>;
  protected abstract calculateAnimationDuration(duration: number): number;
}
